import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MantineProvider, createTheme } from '@mantine/core';
import { Amplify } from 'aws-amplify';

import { AuthProvider } from './auth/auth-context';
import { App } from './App';

import '@mantine/core/styles.css';
import './styles/tokens.css';
import './styles/print.css';

// ---------------------------------------------------------------------------
// AWS Amplify v6 — Cognito authorization code flow with PKCE
// ---------------------------------------------------------------------------
const cognitoRegion = import.meta.env['VITE_COGNITO_REGION'] as string | undefined;
const userPoolId = import.meta.env['VITE_COGNITO_USER_POOL_ID'] as string | undefined;
const userPoolClientId = import.meta.env['VITE_COGNITO_CLIENT_ID'] as string | undefined;
const cognitoDomain = import.meta.env['VITE_COGNITO_DOMAIN'] as string | undefined;

if (userPoolId && userPoolClientId) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
        loginWith: {
          oauth: {
            domain: cognitoDomain
              ? `${cognitoDomain}.auth.${cognitoRegion ?? 'us-west-2'}.amazoncognito.com`
              : '',
            scopes: ['openid', 'email', 'profile'],
            redirectSignIn: [`${window.location.origin}/`],
            redirectSignOut: [`${window.location.origin}/`],
            responseType: 'code', // WHY: Authorization code flow with PKCE per security requirements.
          },
        },
      },
    },
  });
}

// ---------------------------------------------------------------------------
// TanStack Query
// ---------------------------------------------------------------------------
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30 seconds default
      retry: 1,
      refetchOnWindowFocus: false, // WHY: Snapshot data is point-in-time; live data hooks override per-query.
    },
  },
});

// ---------------------------------------------------------------------------
// Mantine Theme — Fraunces (headings), IBM Plex Sans (body), IBM Plex Mono (code)
// ---------------------------------------------------------------------------
const theme = createTheme({
  fontFamily: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontFamilyMonospace: '"IBM Plex Mono", ui-monospace, "Cascadia Code", monospace',
  headings: {
    fontFamily: '"Fraunces", "Georgia", serif',
  },
  primaryColor: 'blue',
});

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------
const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element not found');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={theme}>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </MantineProvider>
    </QueryClientProvider>
  </StrictMode>,
);
